import { Component, OnInit, OnDestroy, AfterViewInit, PLATFORM_ID, ElementRef, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { NavbarComponent } from '@cnt-workspace/ui';
import { FooterComponent } from '@cnt-workspace/ui';
import { MiniMapComponent } from '../mini-map/mini-map.component';
import { BookingCancelModalComponent } from './cancel-modal/booking-cancel-modal.component';
import { BookingModifyModalComponent } from './modify-modal/booking-modify-modal.component';
import {
  AuthService, BookingService, CurbNTurfCashService, IPrivateListing, ListingCatalogService, POLICY_LABEL,
  SeoService, ToastService, canModifyBooking, formatDateRange, modifyDeadline, normalizePolicy, parseBookingDate,
  policySummary, roundMoney,
} from '@cnt-workspace/data-access';
import { IBooking, STATUS_META } from '@cnt-workspace/models';
import { generateReservationCntPayment } from '@curbnturf/helpers';
import { stringFromError } from '@curbnturf/form-helpers/src/lib/helpers/text';
import type { IReservation } from '@curbnturf/entities';
import { gsap } from 'gsap';

function formatShortDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

@Component({
  selector: 'cnt-booking-confirm',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NavbarComponent, FooterComponent, MiniMapComponent, BookingCancelModalComponent, BookingModifyModalComponent],
  templateUrl: './booking-confirm.component.html',
  styleUrls: ['./booking-confirm.component.scss'],
})
export class BookingConfirmComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId = inject<object>(PLATFORM_ID);
  private route = inject(ActivatedRoute);
  private bookingSvc = inject(BookingService);
  private auth = inject(AuthService);
  private seo = inject(SeoService);
  private toasts = inject(ToastService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private catalog = inject(ListingCatalogService);
  private cash = inject(CurbNTurfCashService);
  private catalogSub: Subscription | null = null;
  private cashSub: Subscription | null = null;
  private catalogPrivateListings: IPrivateListing[] = [];

  booking: IBooking | null = null;
  STATUS_META = STATUS_META;
  guestVerified = false;

  private bookingsSub: Subscription | null = null;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') || '';
    this.booking = this.bookingSvc.getById(id);
    this.guestVerified = !!this.auth.currentUser?.verified;
    this.loadRealCreditApplied();
    this.catalogSub = this.catalog.getCatalog$().subscribe(c => {
      this.catalogPrivateListings = c.listings;
      // Backfill lat/lng for bookings created before today's coords change.
      if (this.booking && (this.booking.lat == null || this.booking.lng == null)) {
        const listing = this.catalogPrivateListings.find(l => l.id === this.booking!.listingId);
        if (listing) {
          this.booking = { ...this.booking, lat: listing.coordinates.lat, lng: listing.coordinates.lon };
        }
      }
    });
    this.seo.update({
      title: this.booking ? 'Booking confirmed — CurbNTurf' : 'Booking not found — CurbNTurf',
      description: 'Your CurbNTurf booking confirmation.',
      url: `/booking/confirm/${id}`,
      robots: 'noindex, nofollow',
    });

    // React to status flips (host decision) without requiring a refresh. Also
    // resolves a real booking on a hard refresh: those load asynchronously, so
    // `getById` above comes back null until `refreshRealBookings` lands.
    this.bookingsSub = this.bookingSvc.bookings$.subscribe(all => {
      const updated = all.find(b => b.id === id);
      if (!updated) return;
      if (!this.booking) {
        this.booking = updated;
        this.loadRealCreditApplied();
        return;
      }
      const prevStatus = this.booking.status;
      // Preserve backfilled lat/lng and the separately-loaded credit.
      this.booking = { lat: this.booking.lat, lng: this.booking.lng, creditApplied: this.booking.creditApplied, ...updated };
      if (prevStatus === 'pending' && updated.status === 'approved') {
        this.fireConfetti();
      }
    });
  }

  ngOnDestroy(): void {
    this.bookingsSub?.unsubscribe();
    this.catalogSub?.unsubscribe();
    this.cashSub?.unsubscribe();
  }

  /** Real bookings don't carry `creditApplied` from `toBookingView()` — the server
   *  auto-applies CurbNTurf Cash as a separate ledger transaction rather than baking it
   *  into `priceComponents`. Find the matching transaction the same way the legacy
   *  curbnturf app does (`reservation-card.component.ts`'s `setPriceDisplay()`): match by
   *  the deterministic note `generateReservationCntPayment()` produces for this
   *  reservation, among the user's own cash transactions. */
  private loadRealCreditApplied(): void {
    const userId = this.auth.currentUser?.id;
    const reservationId = this.booking?.realReservationId;
    if (!userId || !reservationId || this.cashSub) return;
    const note = generateReservationCntPayment({ id: reservationId } as IReservation);
    this.cashSub = this.cash.getCreditHistory(userId).subscribe(history => {
      const applied = history.find(t => t.note === note && t.type === 'spent');
      if (applied && this.booking) {
        this.booking = { ...this.booking, creditApplied: applied.amount };
      }
    });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || !this.booking) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    // Entrance animation
    gsap.from('.confirm-anim', { opacity: 0, y: 18, duration: 0.55, ease: 'power3.out', stagger: 0.07, delay: 0.05, clearProps: 'opacity,transform' });
    gsap.from('.confirm-checkmark', { scale: 0.4, opacity: 0, duration: 0.5, ease: 'back.out(2)', delay: 0.2, clearProps: 'opacity,transform' });
    // Confetti — only on first render of a confirmed booking
    if (this.booking.status === 'confirmed') this.fireConfetti();
  }

  private fireConfetti(): void {
    const root = this.host.nativeElement.querySelector('.confetti-root') as HTMLElement | null;
    if (!root) return;
    const colors = ['#e3530d', '#fbd784', '#295d42', '#ffffff'];
    for (let i = 0; i < 36; i++) {
      const piece = document.createElement('span');
      piece.className = 'confetti-piece';
      piece.style.background = colors[i % colors.length];
      piece.style.left = Math.random() * 100 + '%';
      root.appendChild(piece);
      const xDrift = (Math.random() - 0.5) * 240;
      const fall = 280 + Math.random() * 220;
      gsap.fromTo(piece,
        { y: -20, opacity: 1, rotate: Math.random() * 360 },
        { y: fall, x: xDrift, rotate: Math.random() * 720, opacity: 0, duration: 1.6 + Math.random() * 0.6, ease: 'power1.in', delay: Math.random() * 0.3, onComplete: () => piece.remove() }
      );
    }
  }

  get confirmationNumber(): string {
    return this.booking ? this.booking.id.slice(0, 8).toUpperCase() : '';
  }

  get hostInitials(): string {
    if (!this.booking) return '';
    const parts = this.booking.hostName.split(' ').filter(Boolean);
    return parts.map(s => s.charAt(0)).join('').slice(0, 2).toUpperCase();
  }

  /** Booking dates are a bare `YYYY-MM-DD` for every real reservation, and
   *  `new Date()` would read that as UTC midnight — the previous day in every
   *  US timezone. `parseBookingDate` handles both shapes; see F17. */
  private bookingDate(iso: string): Date {
    return parseBookingDate(iso) ?? new Date(iso);
  }

  get datesLabel(): string {
    if (!this.booking) return '';
    const start = this.bookingDate(this.booking.dates.start);
    const end = this.bookingDate(this.booking.dates.end);
    return formatDateRange(start, end, { includeYear: true });
  }

  /** ============ Action bar ============ */

  downloadIcs(): void {
    if (!this.booking || !isPlatformBrowser(this.platformId)) return;
    const b = this.booking;
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    // A stay is an ALL-DAY event, so it takes DATE values rather than an
    // instant. Emitting `DTSTART:20260620T000000Z` put the arrival at 8pm the
    // previous evening in the guest's calendar (and at some other hour in each
    // timezone). `VALUE=DATE` has no timezone to get wrong. DTEND is exclusive
    // for all-day events, which is exactly what `dates.end` already is — the
    // checkout day. See F17.
    const day = (iso: string) => {
      const d = this.bookingDate(iso);
      return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    };
    const start = day(b.dates.start);
    const end = day(b.dates.end);
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//CurbNTurf//Booking//EN',
      'BEGIN:VEVENT',
      `UID:${b.id}@curbnturf`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART;VALUE=DATE:${start}`,
      `DTEND;VALUE=DATE:${end}`,
      `SUMMARY:CurbNTurf — ${b.listingTitle}`,
      `DESCRIPTION:Hosted by ${b.hostName}. Confirmation #${this.confirmationNumber}.`,
      `LOCATION:${b.listingLocation}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `curbnturf-${this.confirmationNumber}.ics`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    this.toasts.success('Calendar event downloaded');
  }

  shareTrip(): void {
    if (!isPlatformBrowser(this.platformId) || !this.booking) return;
    const url = window.location.href;
    const text = `Booked ${this.booking.listingTitle} on CurbNTurf — ${this.datesLabel}`;
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
    if (nav.share) {
      nav.share({ title: 'My CurbNTurf trip', text, url }).catch(() => { /* noop */ });
    } else {
      navigator.clipboard?.writeText(url).then(() => this.toasts.success('Trip link copied to clipboard'));
    }
  }

  printReceipt(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.print();
  }

  // ============ Cancel / modify (dialogs are sub-components) ============

  cancelOpen = false;
  modifyOpen = false;

  /** Guest or host can cancel anything live before check-in. */
  get isCancellable(): boolean {
    if (!this.booking) return false;
    if (this.booking.status === 'cancelled' || this.booking.status === 'declined') return false;
    // Hosts decline (not cancel) a request; they cancel only what they accepted.
    if (this.isHostViewer && this.booking.status === 'pending' && !this.booking.changeRequested) return false;
    return this.bookingDate(this.booking.dates.start).getTime() > Date.now();
  }

  /** Server-mirrored change window (see `canModifyBooking`). Guests only. */
  get isModifiable(): boolean {
    return !!this.booking && !this.isHostViewer && canModifyBooking(this.booking);
  }

  /** Why Modify isn't offered, when the guest would otherwise expect it. */
  get modifyBlockedReason(): string | null {
    const b = this.booking;
    if (!b?.realReservationId || this.isHostViewer || this.isModifiable) return null;
    if (b.status === 'cancelled' || b.status === 'declined') return null;
    if (b.changeRequested) return 'A change is already waiting on the host.';
    if (b.status === 'pending') return 'You can change dates once the host accepts.';
    if (normalizePolicy(b.cancellationPolicy) === 'exclusive') return 'This listing’s Exclusive policy doesn’t allow changes.';
    const deadline = modifyDeadline(b);
    return deadline ? `Changes closed ${formatShortDate(deadline)} under the listing’s policy.` : null;
  }

  openCancelModal(): void { this.cancelOpen = true; }
  openModifyModal(): void { if (this.booking) this.modifyOpen = true; }

  onCancelled(updated: IBooking): void {
    this.cancelOpen = false;
    this.booking = { lat: this.booking?.lat, lng: this.booking?.lng, ...updated };
  }

  onModified(updated: IBooking): void {
    this.modifyOpen = false;
    this.booking = { lat: this.booking?.lat, lng: this.booking?.lng, creditApplied: this.booking?.creditApplied, ...updated };
  }

  // ============ Host view ============

  /** The listing owner is looking at their guest's booking. */
  get isHostViewer(): boolean {
    const me = this.auth.currentUser?.id;
    return !!me && !!this.booking?.hostUserId && this.booking.hostUserId === me && this.booking.guestUserId !== me;
  }

  /** A new request or a change request waiting on this host. */
  get awaitingHostDecision(): boolean {
    return this.isHostViewer && this.booking?.status === 'pending';
  }

  deciding = false;
  declineOpen = false;
  declineReason = '';

  async decide(decision: 'approved' | 'declined'): Promise<void> {
    if (!this.booking || this.deciding) return;
    this.deciding = true;
    try {
      const updated = await this.bookingSvc.hostDecide(this.booking.id, decision, decision === 'declined' ? this.declineReason : '');
      if (updated) {
        this.booking = { lat: this.booking.lat, lng: this.booking.lng, ...updated };
        this.declineOpen = false;
        this.declineReason = '';
      } else {
        this.toasts.error('Could not save your decision — please try again.');
      }
    } catch (err) {
      this.toasts.error(err instanceof HttpErrorResponse
        ? stringFromError(err) || 'Could not save your decision — please try again.'
        : 'Could not save your decision — please try again.');
      console.error('[booking-confirm] host decision failed', err);
    } finally {
      this.deciding = false;
    }
  }

  // ============ Pricing & policy ============

  /** "Requested dates" label while a change waits on the host. */
  get pendingDatesLabel(): string {
    const p = this.booking?.pendingChange;
    if (!p?.start || !p?.end) return '';
    return formatDateRange(this.bookingDate(p.start), this.bookingDate(p.end), { includeYear: true });
  }

  /** What went on the card — total less CurbNTurf Cash applied. */
  get paidByCard(): number {
    if (!this.booking) return 0;
    return roundMoney(Math.max(0, this.booking.total - (this.booking.creditApplied || 0)));
  }

  get policyLabel(): string {
    return POLICY_LABEL[normalizePolicy(this.booking?.cancellationPolicy)];
  }

  get policyText(): string {
    return policySummary(this.booking?.cancellationPolicy);
  }
}
